/**
 * AppController: the shell's single source of runtime state. Owns the engine host, the active
 * world instance, the audio engine, preferences and the quality governor, and exposes a
 * snapshot for React (useSyncExternalStore).
 */
import { AudioEngine } from '../framework/audio/AudioEngine';
import { EngineHost, type GraphicsFailure } from '../framework/engine/EngineHost';
import { QualityGovernor } from '../framework/quality/governor';
import { applyOverrides, MANUAL_DETAIL, PREFERENCE_PROFILES, QUALITY_LADDER, resolveFrameCap, resolveGeometry } from '../framework/quality/profiles';
import type { TimeMode } from '../framework/sim/clock';
import type { LightStage } from '../framework/sim/lightModel';
import type { WeatherMood } from '../framework/sim/weather';
import type { VisitorInfo, WorldInstance, WorldSettings, WorldStatus } from '../framework/world/contract';
import type { WorldManifest } from '../framework/world/manifest';
import { catalog, type CatalogEntry } from '../worlds/registry';
import { assetUrl } from './assets';
import { MusicController } from './music';
import { PrefsStore, type Prefs } from './prefs';
import { saveScreenshot, type ScreenshotResult } from './screenshot';

export type AppPhase = 'booting' | 'unsupported' | 'loading' | 'ready' | 'error';
/** The error when the graphics failed repeatedly and the shell stays on the still picture. */
export const GRAPHICS_GAVE_UP = 'the graphics stopped responding several times';
export type AppView = 'gallery' | 'world';

const STAGE_WORDS: Record<LightStage, string> = { dawn: 'dawn', morning: 'morning', afternoon: 'afternoon', golden: 'early evening', dusk: 'dusk', night: 'night' };

export interface AppSnapshot {
  phase: AppPhase;
  view: AppView;
  progress: number;
  progressLabel: string;
  worldId: string;
  manifest: WorldManifest | null;
  status: WorldStatus | null;
  qualityLevel: number;
  /** Frame-rate readout (when turned on in Settings): frames per second and render size. */
  frameStats: { fps: number; width: number; height: number } | null;
  geometryPending: boolean;
  /** Bumped each time the graphics are rebuilt on a fresh canvas (the shell keys the canvas by it). */
  canvasGeneration: number;
  /** A one-off message for the viewer (shown as a toast, then cleared). */
  notice: string | null;
  /** Creatures that can be invited (Settings → Invite a visitor), with their state now. */
  visitors: VisitorInfo[];
  /** The graphics context is lost: the poster covers the stage until it is restored or rebuilt. */
  graphicsLost: boolean;
  reducedMotion: boolean;
  error: string | null;
  devTools: boolean;
}

type Listener = () => void;

export class AppController {
  readonly prefs = new PrefsStore();
  readonly audio = new AudioEngine();
  readonly music: MusicController;
  readonly devTools: boolean;
  private host: EngineHost | null = null;
  private world: WorldInstance | null = null;
  private governor: QualityGovernor | null = null;
  private listeners = new Set<Listener>();
  private snap: AppSnapshot;
  private statusTimer = 0;
  private fpsFrames = 0;
  private fpsMs = 0;
  private fpsSince = 0;
  private loadedGeometry: 'low' | 'medium' | 'high' | null = null;
  /** Vegetation density the habitat was built with (residents follow density live). */
  private loadedDensity: string | null = null;
  private reducedMotionQuery: MediaQueryList | null = null;
  private seed: number;
  private disposed = false;
  /** Canvas generation the current host was built on. */
  private attachedGeneration = -1;
  /** When the graphics were rebuilt after a failure, and whether the background caused it (recent ones only). */
  private rebuilds: Array<{ at: number; background: boolean }> = [];
  /** Highest ladder level for the rest of the session (lowered after a graphics failure). */
  private levelCap = Infinity;
  /** Whether the world was last suspended for a hidden page (see `syncVisibility`). */
  private suspendedForHidden = false;

  constructor() {
    const params = new URLSearchParams(location.search);
    this.devTools = import.meta.env.DEV || params.has('gbr-dev');
    this.seed = params.has('seed') && this.devTools ? Number(params.get('seed')) : (Date.now() ^ 0x5f3759df) >>> 0;
    const first = catalog.find((c) => c.manifest.id === this.prefs.get().lastWorld) ?? catalog[0];
    this.snap = {
      phase: 'booting',
      view: 'gallery',
      progress: 0,
      progressLabel: '',
      worldId: first.manifest.id,
      manifest: first.manifest,
      status: null,
      qualityLevel: 0,
      frameStats: null,
      geometryPending: false,
      canvasGeneration: 0,
      notice: null,
      visitors: [],
      graphicsLost: false,
      reducedMotion: false,
      error: null,
      devTools: this.devTools,
    };
    this.music = new MusicController(this.audio, this.prefs);
    this.music.setPlaylist(first.manifest.playlist, first.manifest.id, false);
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotionQuery.addEventListener('change', () => this.applySettings());
    }
    this.snap.reducedMotion = this.computeReducedMotion();
    this.prefs.subscribe(() => this.onPrefsChanged());
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('pageshow', this.onVisibility);
  }

  // --- React store protocol -------------------------------------------------------------
  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): AppSnapshot => this.snap;

  private set(patch: Partial<AppSnapshot>): void {
    this.snap = { ...this.snap, ...patch };
    this.listeners.forEach((fn) => fn());
  }

  get catalog(): CatalogEntry[] {
    return catalog.filter((c) => this.devTools || !c.manifest.devOnly);
  }

  get activeWorld(): WorldInstance | null {
    return this.world;
  }

  get engine(): EngineHost | null {
    return this.host;
  }

  // --- Lifecycle -----------------------------------------------------------------------
  async init(canvas: HTMLCanvasElement): Promise<void> {
    await this.attach(canvas, 0);
  }

  /**
   * Build the graphics on a canvas and load the world into them: on start, and on each fresh
   * canvas after a graphics failure (generation > 0).
   */
  async attach(canvas: HTMLCanvasElement, generation: number): Promise<void> {
    if (this.disposed || generation <= this.attachedGeneration || generation !== this.snap.canvasGeneration) return;
    this.attachedGeneration = generation;
    const host = await EngineHost.create(canvas);
    if (this.disposed) {
      host?.destroy();
      return;
    }
    if (!host) {
      this.set({ phase: generation === 0 ? 'unsupported' : 'error', error: generation === 0 ? null : 'the graphics could not be restarted' });
      return;
    }
    this.host = host;
    host.start();
    host.onFailure((reason) => this.rebuildGraphics(reason));
    host.onLost(() => this.set({ graphicsLost: true }));
    host.onRestore(() => {
      this.set({ graphicsLost: false });
      this.governor?.restartWarmup(true);
    });
    host.onUpdate(({ rendering, frameMs, steady }) => {
      // Frames arrive only while the page is visible: a world still suspended for a hidden page
      // means the browser never said the page came back (seen on iOS). Resume it now.
      if (this.suspendedForHidden && !document.hidden) this.onVisibility();
      if (rendering && steady && this.governor) {
        const d = this.governor.sample(frameMs);
        if (d.changed) this.applySettings();
      }
      this.statusTimer -= 1;
      if (this.statusTimer <= 0 && this.world) {
        this.statusTimer = 15;
        this.set({ status: this.world.status(), visitors: this.world.visitors?.() ?? [] });
      }
      this.sampleFrameRate(rendering, frameMs);
    });
    await this.loadWorld(this.snap.worldId);
  }

  /**
   * The graphics could not recover in place (a GPU reset the browser would not restore, or
   * rendering failing every frame): drop the world and the host, and build them again on a fresh
   * canvas at lighter detail. The poster covers the stage meanwhile; music plays on. After
   * repeated failures, stay on the still picture and say so.
   *
   * A context the browser took away while the page was in the background (iOS frees them) says
   * nothing about whether this device copes: the picture is rebuilt at the same detail, and only
   * an unusual run of such losses counts toward giving up.
   */
  private rebuildGraphics(reason: GraphicsFailure): void {
    if (this.disposed) return;
    const now = performance.now();
    const background = reason === 'context-lost-in-background';
    this.rebuilds = this.rebuilds.filter((r) => now - r.at < 15 * 60_000);
    this.rebuilds.push({ at: now, background });
    if (!background) this.levelCap = Math.max(0, Math.min(this.levelCap, this.snap.qualityLevel - 2));
    console.warn(`[GBR] rebuilding the graphics (${reason})${Number.isFinite(this.levelCap) ? `; detail capped at level ${this.levelCap} for this session` : ''}`);
    this.world?.dispose();
    this.world = null;
    this.host?.destroy();
    this.host = null;
    const failures = this.rebuilds.filter((r) => !r.background).length;
    if (failures > 3 || this.rebuilds.length > 10) {
      this.set({ phase: 'error', error: GRAPHICS_GAVE_UP, frameStats: null });
      return;
    }
    this.set({
      phase: 'loading',
      progress: 0,
      progressLabel: 'Restarting the picture',
      frameStats: null,
      graphicsLost: false,
      canvasGeneration: this.snap.canvasGeneration + 1,
      notice: background
        ? 'The browser let go of the graphics while this page was in the background, so the picture was restarted.'
        : 'The graphics stopped responding, so the picture was restarted with lighter detail.',
    });
  }

  clearNotice(): void {
    if (this.snap.notice) this.set({ notice: null });
  }

  private resolveSettings(): WorldSettings {
    const p = this.prefs.get();
    const profile = PREFERENCE_PROFILES[p.visual.preference];
    let level = profile.startLevel;
    if (p.visual.detail !== 'auto') level = MANUAL_DETAIL[p.visual.detail].level;
    if (this.governor && p.visual.autoPerformance) level = this.governor.current;
    level = Math.min(level, this.levelCap);
    return {
      detail: this.loadedGeometry ?? resolveGeometry(p.visual.preference, p.visual.detail),
      density: p.visual.density,
      // The ladder level, with any options the viewer pinned by hand laid over it.
      level: applyOverrides(QUALITY_LADDER[level], p.visual.graphics),
      reducedMotion: this.computeReducedMotion(),
      spatialAudio: p.audio.spatial,
      backgroundWildlife: p.visual.wildlife,
      fallingDebris: p.visual.fallingLeaves,
    };
  }

  private setupGovernor(): void {
    const p = this.prefs.get();
    const profile = PREFERENCE_PROFILES[p.visual.preference];
    let min = profile.minLevel;
    let max = profile.maxLevel;
    let start = profile.startLevel;
    if (p.visual.detail !== 'auto') {
      max = MANUAL_DETAIL[p.visual.detail].level;
      start = max;
      min = Math.max(0, max - 2);
    }
    max = Math.min(max, this.levelCap);
    min = Math.min(min, max);
    start = Math.min(start, max);
    const cap = resolveFrameCap(p.visual.preference, p.visual.graphics);
    const targetFps = cap === 30 ? 30 : 60;
    if (!p.visual.autoPerformance) {
      this.governor = null;
    } else if (!this.governor) {
      this.governor = new QualityGovernor({ minLevel: min, maxLevel: max, startLevel: start, targetFps });
    } else {
      this.governor.setBounds(min, max, targetFps);
    }
    this.host?.setFrameCap(cap);
  }

  async loadWorld(id: string): Promise<void> {
    const entry = catalog.find((c) => c.manifest.id === id);
    const host = this.host;
    if (!entry || !host) return;
    this.world?.dispose();
    this.world = null;
    this.set({ phase: 'loading', progress: 0, progressLabel: 'Opening', worldId: id, manifest: entry.manifest, error: null });
    try {
      const mod = await entry.load();
      const p = this.prefs.get();
      this.setupGovernor();
      this.loadedGeometry = resolveGeometry(p.visual.preference, p.visual.detail);
      this.loadedDensity = p.visual.density;
      const world = mod.create({
        host,
        audio: this.audio,
        assetUrl,
        dev: this.devTools,
        seed: this.seed,
        settings: this.resolveSettings(),
        initialTime: { mode: p.time.mode, heldHour: p.time.heldHour },
        initialWeather: p.weather.mood,
      });
      await world.load((progress, label) => this.set({ progress, progressLabel: label }));
      // Disposed, or the graphics were rebuilt while loading (a newer load follows).
      if (this.disposed || this.host !== host) {
        world.dispose();
        return;
      }
      this.world = world;
      world.enter(this.snap.view === 'world' ? 'immersive' : 'preview');
      // A new world starts running; suspend it at once if the page is hidden.
      this.suspendedForHidden = false;
      if (document.hidden) this.onVisibility();
      // Load-time hitches are shader compiles, which the host leaves out; a slow device must be
      // caught as soon as the warm-up ends, so no settling here.
      this.governor?.restartWarmup();
      this.prefs.update((d) => {
        d.lastWorld = id;
      });
      this.music.setPlaylist(entry.manifest.playlist, id, true);
      this.set({ phase: 'ready', progress: 1, status: world.status(), geometryPending: false });
    } catch (err) {
      console.error('[GBR] world load failed', err);
      this.set({ phase: 'error', error: err instanceof Error ? err.message : String(err) });
    }
  }

  enterWorld(): void {
    this.world?.setViewMode('immersive');
    this.set({ view: 'world' });
  }

  exitToGallery(): void {
    this.world?.setViewMode('preview');
    this.set({ view: 'gallery' });
  }

  // --- World controls -------------------------------------------------------------------
  setTimeMode(mode: TimeMode): void {
    this.world?.setTimeMode(mode);
    const st = this.world?.status();
    this.prefs.update((d) => {
      d.time.mode = mode;
      if (mode === 'hold' && st) d.time.heldHour = st.heldHour;
    });
    this.refreshStatus();
  }

  jumpToStage(stage: LightStage): void {
    this.world?.jumpToStage(stage);
    this.prefs.update((d) => {
      if (d.time.mode === 'local') d.time.mode = 'living';
      if (d.time.mode === 'hold') {
        d.time.heldStage = stage;
        d.time.heldHour = undefined;
      }
    });
    // Persist the exact held hour after the transition settles.
    window.setTimeout(() => {
      const st = this.world?.status();
      if (st && this.prefs.get().time.mode === 'hold') this.prefs.update((d) => (d.time.heldHour = st.heldHour));
    }, 9000);
    this.refreshStatus();
  }

  /**
   * Invite a creature in (Settings → Invite a visitor). It comes as it naturally would; outside its
   * hours the time of day moves there first (the same as choosing a time of day), and it comes
   * once the light has arrived.
   */
  inviteVisitor(id: string): void {
    const world = this.world;
    if (!world?.invite) return;
    const label = world.visitors?.().find((v) => v.id === id)?.label ?? 'A visitor';
    const r = world.invite(id);
    if (r.state === 'later') {
      this.jumpToStage(r.stage);
      world.invite(id, { whenNatural: true });
      this.set({ notice: `${label}: moving the time of day to ${STAGE_WORDS[r.stage]}. It will come then.` });
    } else if (r.state === 'coming') {
      this.set({ notice: `${label} is on its way.` });
    } else if (r.state === 'unavailable') {
      this.set({ notice: `${label} is not coming just now: ${r.reason}.` });
    }
    this.set({ visitors: world.visitors?.() ?? [] });
  }

  setWeatherMood(mood: WeatherMood): void {
    this.world?.setWeatherMood(mood);
    this.prefs.update((d) => (d.weather.mood = mood));
    this.refreshStatus();
  }

  zoomBy(f: number): void {
    this.world?.zoomBy(f);
  }

  async screenshot(): Promise<ScreenshotResult> {
    if (!this.world || !this.snap.manifest) return { ok: false, message: 'Nothing to capture yet.' };
    try {
      const blob = await this.world.captureFrame();
      return await saveScreenshot(blob, this.snap.manifest.title);
    } catch (err) {
      return { ok: false, message: err instanceof Error ? err.message : 'Capture failed.' };
    }
  }

  private refreshStatus(): void {
    if (this.world) this.set({ status: this.world.status() });
  }

  // --- Settings ---------------------------------------------------------------------------
  private computeReducedMotion(): boolean {
    const pref = this.prefs.get().a11y.reducedMotion;
    if (pref === 'on') return true;
    if (pref === 'off') return false;
    return this.reducedMotionQuery?.matches ?? false;
  }

  /** Frames per second from the rendered frames' own intervals, refreshed twice a second. */
  private sampleFrameRate(rendering: boolean, frameMs: number): void {
    if (!this.prefs.get().visual.showFrameRate || !this.host) {
      if (this.snap.frameStats) this.set({ frameStats: null });
      return;
    }
    const now = performance.now();
    if (rendering && frameMs > 0 && frameMs < 5000) {
      this.fpsFrames++;
      this.fpsMs += frameMs;
    }
    if (this.fpsSince === 0) this.fpsSince = now;
    if (now - this.fpsSince < 500 || this.fpsFrames === 0) return;
    const fps = (this.fpsFrames * 1000) / this.fpsMs;
    this.fpsFrames = 0;
    this.fpsMs = 0;
    this.fpsSince = now;
    const { width, height } = this.host.pixelSize;
    this.set({ frameStats: { fps: fps >= 10 ? Math.round(fps) : Math.round(fps * 10) / 10, width, height } });
  }

  private onPrefsChanged(): void {
    this.setupGovernor();
    this.applySettings();
  }

  private applySettings(): void {
    const s = this.resolveSettings();
    this.world?.applySettings(s);
    const p = this.prefs.get();
    const wanted = resolveGeometry(p.visual.preference, p.visual.detail);
    this.set({ qualityLevel: s.level.index, reducedMotion: s.reducedMotion, geometryPending: this.loadedGeometry !== null && (wanted !== this.loadedGeometry || (this.loadedDensity !== null && p.visual.density !== this.loadedDensity)) });
  }

  /** Reload the world to apply a changed geometry detail (user-initiated). */
  async applyGeometryNow(): Promise<void> {
    this.loadedGeometry = null;
    this.loadedDensity = null;
    await this.loadWorld(this.snap.worldId);
  }

  /**
   * Suspend the world while the page is hidden, and resume it when it shows again: from the
   * visibility event, from `pageshow`, and from the next frame if neither came (iOS).
   */
  private onVisibility = (): void => {
    if (!this.world) return;
    if (document.hidden) {
      if (this.suspendedForHidden) return;
      this.suspendedForHidden = true;
      const keep = this.prefs.get().audio.playWhenHidden;
      this.world.suspend(keep);
      if (!keep) this.music.pauseForHidden();
    } else {
      if (!this.suspendedForHidden) return;
      this.suspendedForHidden = false;
      this.world.resume();
      this.music.resumeFromHidden();
      // The first seconds back are slow (the GPU wakes, iOS may have released shaders): let the
      // frame rate recover before the governor judges it.
      this.governor?.restartWarmup(true);
    }
  };

  get prefsValue(): Prefs {
    return this.prefs.get();
  }

  dispose(): void {
    this.disposed = true;
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('pageshow', this.onVisibility);
    this.world?.dispose();
    this.music.dispose();
    this.host?.destroy();
  }
}
